/**
 * Syncs web deep links with app selection state for student routes.
 */
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import type { NavigationContainerRefWithCurrent } from '@react-navigation/native';
import { useAppStateStore } from '@/common/context/AppStateStore';
import { isUUID } from '@/common/types/ids';
import log from '@/common/utils/logger';

type RootParamList = Record<string, object | undefined>;

type NavigationStateLike = {
  index?: number;
  routes?: Array<NavigationRouteLike>;
};

type NavigationRouteLike = {
  name: string;
  params?: Record<string, unknown>;
  state?: NavigationStateLike;
};

type UseDeepLinkHandlerProps = {
  navigationRef?: NavigationContainerRefWithCurrent<RootParamList>;
};

const isValidIndex = (index: number | undefined, length: number) => (
  typeof index === 'number' && index >= 0 && index < length
);

/**
 * Returns the deepest active route from a navigation state.
 */
const getLeafRoute = (state: NavigationStateLike | undefined): NavigationRouteLike | null => {
  if (!state?.routes || !state.routes.length) return null;
  if (!isValidIndex(state.index, state.routes.length)) return null;

  let route = state.routes[state.index as number];
  while (route?.state?.routes && route.state.routes.length && isValidIndex(route.state.index, route.state.routes.length)) {
    route = route.state.routes[route.state.index as number];
  }
  return route || null;
};

/**
 * Handles deep link based state sync on web.
 */
const useDeepLinkHandler = ({ navigationRef }: UseDeepLinkHandlerProps) => {
  const setSelectedTask = useAppStateStore((state) => state.setSelectedTask);
  const setSelectedModule = useAppStateStore((state) => state.setSelectedModule);
  const invalidStateLogCountRef = useRef(0);

  /**
   * Logs a limited breadcrumb for unexpected navigation state shapes.
   */
  const logInvalidState = (reason: string) => {
    if (invalidStateLogCountRef.current >= 3) return;
    invalidStateLogCountRef.current += 1;
    log.warn('Deep link navigation state invalid', { reason });
  };

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;

    const syncStoreWithNavigation = () => {
      if (!navigationRef?.current?.isReady()) return;

      const state = navigationRef.current.getRootState() as NavigationStateLike | undefined;
      if (!state) {
        logInvalidState('missing_root_state');
        return;
      }

      const route = getLeafRoute(state);
      if (!route) {
        logInvalidState('missing_leaf_route');
        return;
      }

      if (!route.params) return;

      const { moduleId, taskId, courseId } = route.params as {
        moduleId?: string;
        taskId?: string;
        courseId?: string;
      };

      // Redirect if params are undefined (string)
      // Only redirect if we are on a student detail screen to avoid breaking other roles
      if (moduleId === 'undefined' || courseId === 'undefined') {
        const studentDetailRoutes = ['ModuleScreen', 'TaskScreen', 'GradeScreen'];
        if (studentDetailRoutes.includes(route.name)) {
          // Attempt recovery from store if possible before redirecting to CourseScreen
          const currentStore = useAppStateStore.getState();
          const { selectedModule } = currentStore;

          if (selectedModule?.moduleId && selectedModule?.courseId) {
            const targetRoute = route.name as keyof RootParamList;
            const targetParams = {
              ...route.params,
              moduleId: selectedModule.moduleId,
              courseId: selectedModule.courseId,
            } as RootParamList[keyof RootParamList];
            navigationRef.current?.navigate(targetRoute, targetParams);
            return;
          }

          navigationRef.current?.navigate('CourseScreen' as keyof RootParamList);
        }
        return;
      }

      // sync navigation state from link to appState
      if (moduleId && courseId) {
        const currentStore = useAppStateStore.getState();
        const { selectedModule } = currentStore;
        const { selectedTaskIds } = currentStore;

        // Validate IDs at boundary (from navigation params)
        if (!isUUID(moduleId) || !isUUID(courseId)) {
          // Invalid IDs detected - attempt recovery or redirect to safe state
          log.warn('[DeepLinkHandler] Invalid IDs in navigation params - recovering', {
            moduleId,
            courseId,
            routeName: route.name,
          });

          // Attempt recovery from store if we have valid IDs there
          if (selectedModule?.moduleId && selectedModule?.courseId) {
            const targetRoute = route.name as keyof RootParamList;
            const targetParams = {
              ...route.params,
              moduleId: selectedModule.moduleId,
              courseId: selectedModule.courseId,
            } as RootParamList[keyof RootParamList];
            navigationRef.current?.navigate(targetRoute, targetParams);
            return;
          }

          // No valid store data - redirect to CourseScreen to clear bad state
          const studentDetailRoutes = ['ModuleScreen', 'TaskScreen', 'GradeScreen'];
          if (studentDetailRoutes.includes(route.name)) {
            navigationRef.current?.navigate('CourseScreen' as keyof RootParamList);
          }
          return;
        }

        // if passed moduleId does not match the persisted state,
        if (moduleId !== selectedModule?.moduleId || courseId !== selectedModule?.courseId) {
          setSelectedModule({ moduleId, courseId });
        }

        // if we have a taskId, and it does not match persisted state, set it
        if (taskId && isUUID(taskId) && taskId !== selectedTaskIds[moduleId]) {
          setSelectedTask(moduleId, taskId);
        }
      }
    };

    // Listen for any state changes in the navigation
    const unsubscribe = navigationRef?.current?.addListener('state', syncStoreWithNavigation);

    // Initial sync
    syncStoreWithNavigation();

    return unsubscribe;
  }, [navigationRef, setSelectedModule, setSelectedTask]);
};

export default useDeepLinkHandler;
